import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Typography,
  Box,
  Container,
  Modal,
  MenuItem,
  Button,
} from "@mui/material";
import ModeEditOutlinedIcon from "@mui/icons-material/ModeEditOutlined";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import ConfirmStopModal from "../../shared/ConfirmStopModal";
import RequestStatus from "../../shared/request/RequestStatus";
import ActionButton from "../../shared/ActionButton";
import NoticeBanner from "../../shared/NoticeBanner";
import SearchContainer from "../shared/search/SearchContainer";
import SearchTabs from "../shared/search/filter/SearchTabs";
import MenuModal from "../../shared/MenuModal";
import Positions from "../shared/search/filter/Positions";
import Price from "../shared/search/filter/Price";
import Prefectures from "../shared/search/filter/Prefecture";
import ModalSp from "../../shared/ModalForSp";
import FilterTag from "../shared/search/FilterTag";
import useActiveFilterTags from "../../shared/hooks/useActiveFilterTags";
import { useFilterModal } from "../../shared/hooks/useFilterModal";
import {
  FilterModalHeader,
  FilterModalFooter,
} from "../../shared/FilterModalParts";
import useRemoveFilterTagAndSearch from "../../shared/hooks/useRemoveFilterTagAndSearch";
import { useIsDesktop } from "../../shared/hooks/useIsDesktop";
import { useSearchableIndex } from "../../../controllers/useSearchableIndex";
import { buildPrefByArea } from "../../utils/prefectureArea";
import { getMasterData } from "../../utils/masterDataClient";
import usePriceFilterInSearchFilters, {
  createInitialPriceFilter,
} from "../../shared/hooks/usePriceFilterInSearchFilters";
import { updateReadFlagById } from "../../utils/updateReadFlagById";
import {
  DEFAULT_PER_PAGE,
  createInitialPagination,
} from "../../utils/pagination";
import {
  JOB_COLOR,
  JOB_COLOR_LIGHT,
  JOB_COLOR_DARK,
  GRAY_TEXT_COLOR,
  TEXT_COLOR_PRIMARY,
  BORDER_COLOR_LIGHT,
} from "../../../constants/colors";
import useRequestStatus from "../shared/hooks/useRequestStatus";

const shouldShowJobSearch = (pathname) => {
  // 求人一覧画面でのみ表示
  return pathname === "/jobs" || pathname === "/jobs/";
};

const createInitialJobSearchFilters = () => ({
  keyword: [],
  keywordInputText: "",
  roleIds: [],
  prefectureIds: [],
  priceFilter: createInitialPriceFilter(),
});

const initialPriceFilter = createInitialPriceFilter();

export default function JobLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const showSearchContainer = shouldShowJobSearch(location.pathname);

  // レスポンシブ
  const theme = useTheme();
  const isDesktop = useIsDesktop();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // 共通hook
  const {
    exists: jobExists,
    isActive,
    isStatusLoading,
    error,
    modalOpen,
    handleChangeStatus,
    handleConfirmStop,
    handleCancelStop,
  } = useRequestStatus({
    fetchPath: "/user_job_request/edit.json",
    togglePath: "/user_job_request/toggle_recruiting",
    existsKey: "job_request_exists",
    eventName: "job:status-updated",
    requestParamKey: "user_job_request",
    errorMessage: "読み込みに失敗しました。再読み込みしてください。",
  });

  // 表示する求人一覧
  const [jobs, setJobs] = useState([]);

  // 一覧取得中フラグ
  const [loading, setLoading] = useState(true);

  // 現在ページ
  const [page, setPage] = useState(1);

  // 通常一覧のページング情報
  const [pagination, setPagination] = useState(
    createInitialPagination(DEFAULT_PER_PAGE),
  );

  const jobsAbortControllerRef = useRef(null);
  const jobsRequestIdRef = useRef(0);

  // スマホ用絞り込みメニューモーダル
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const menuOpen = Boolean(menuAnchorEl);

  // 絞り込みモーダル
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  // 選択中の絞り込み条件
  const [activeFilter, setActiveFilter] = useState(null);

  // ポジション
  const [roles, setRoles] = useState([]);

  // 勤務地
  const [prefectures, setPrefectures] = useState([]);

  const scrollToResultsTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: "auto",
    });
  }, []);

  // /jobs.jsonを取得してjobsを更新
  const fetchJobs = useCallback(async (nextPage = 1) => {
    jobsAbortControllerRef.current?.abort();

    const controller = new AbortController();
    jobsAbortControllerRef.current = controller;
    const requestId = ++jobsRequestIdRef.current;

    setLoading(true);
    try {
      const res = await fetch(
        `/jobs.json?page=${nextPage}&per=${DEFAULT_PER_PAGE}`,
        {
          credentials: "same-origin",
          cache: "no-store",
          signal: controller.signal,
        },
      );

      if (!res.ok) {
        if (requestId !== jobsRequestIdRef.current) return;

        setJobs([]);
        setPagination(createInitialPagination(DEFAULT_PER_PAGE));
        return;
      }

      const json = await res.json();

      if (requestId !== jobsRequestIdRef.current) return;

      setJobs(json.jobs);
      setPagination(
        json.pagination ?? createInitialPagination(DEFAULT_PER_PAGE),
      );
      setPage(json.pagination?.current_page ?? nextPage);
    } catch (error) {
      if (error.name === "AbortError") return;
      if (requestId !== jobsRequestIdRef.current) return;

      setJobs([]);
      setPagination(createInitialPagination(DEFAULT_PER_PAGE));
    } finally {
      if (requestId === jobsRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const {
    searchFilters,
    setSearchFilters,
    hasSearchFilters,
    hasExecutedSearch,
    markSearchAsNotExecuted,
    executeSearch,
    searchResults,
    mutateSearchResults,
    searchPagination,
    isSearching,
  } = useSearchableIndex({
    searchEndpoint: "/api/jobs/search",
    fetchIndex: fetchJobs,
    createInitialSearchFilters: createInitialJobSearchFilters,
    getResponseItems: (json) => json.jobs,
  });

  const { setPriceFilterInSearchFilters } = usePriceFilterInSearchFilters({
    setSearchFilters,
    fallbackPriceFilter: initialPriceFilter,
  });

  const { handleRemoveFilterTagAndSearch } = useRemoveFilterTagAndSearch({
    setSearchFilters,
    executeSearch,
    prefectures,
  });

  // ポジションと都道府県を一覧で取得
  useEffect(() => {
    getMasterData()
      .then((masterData) => {
        setRoles(masterData.roles);
        setPrefectures(masterData.prefectures);
      })
      .catch((error) => {
        console.error("/api/master fetch failed", error);
        toast.error("情報の読み込みに失敗しました。再読み込みしてください。");
        setRoles([]);
        setPrefectures([]);
      });
  }, []);

  // 再レンダリングのたびに分類処理を走らせないため useMemo でキャッシュ
  const prefByArea = useMemo(() => buildPrefByArea(prefectures), [prefectures]);

  // 選択されたポジションを表示(未選択の場合は「ポジション」を表示)
  const selectedRoleLabel = useMemo(() => {
    if (!searchFilters.roleIds?.length) return "";

    const selectedRoleIdSet = new Set(searchFilters.roleIds.map(String));

    const selectedRoleNames = roles
      .filter((role) => selectedRoleIdSet.has(String(role.id)))
      .map((role) => role.name);

    return selectedRoleNames.join("、");
  }, [searchFilters.roleIds, roles]);

  // 選択された金額を表示(金額: ◯万円／未選択の場合は「年収」を表示)
  const selectedIncomeLabel = useMemo(() => {
    const priceValue = String(searchFilters.priceFilter?.value ?? "").trim();
    if (priceValue === "") return "";
    return `${priceValue}万円`;
  }, [searchFilters.priceFilter]);

  // 選択された勤務地を表示(未選択の場合は「勤務地」を表示)
  const selectedPrefectureLabel = useMemo(() => {
    if (!searchFilters.prefectureIds?.length) return "";

    const selectedPrefectureIdSet = new Set(
      searchFilters.prefectureIds.map(String),
    );

    const selectedPrefectureNames = prefectures
      .filter((prefecture) =>
        selectedPrefectureIdSet.has(String(prefecture.id)),
      )
      .map((prefecture) => prefecture.name);

    return selectedPrefectureNames.join("、");
  }, [searchFilters.prefectureIds, prefectures]);

  // PC: 選択・指定した条件をタグにする
  const { activeFilterTags } = useActiveFilterTags({
    searchFilters,
    roles,
    prefectures,
  });

  // スマホならメニューモーダル、PCなら絞り込みモーダルを表示
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  const {
    handleOpen,
    handleClose,
    modalStyle,
    deleteFilter,
    openFilterModal,
    modalTitle,
    modalHeaderSx,
    modalFooterOkButtonSx,
    handleSubmit,
  } = useFilterModal({
    isMobile,
    setMenuAnchorEl,
    setFilterDialogOpen,
    setFilterModalOpen,
    setActiveFilter,
    activeFilter,
    executeSearch,
    searchFilters,
    setSearchFilters,
    selectedColor: TEXT_COLOR_PRIMARY,
    projectColor: JOB_COLOR,
    projectColorDark: JOB_COLOR_DARK,
    filterTitleMap: { price: "年収" },
    scrollToResultsTop,
  });

  const modalFooter = (
    <FilterModalFooter
      onCancel={handleClose}
      onSubmit={handleSubmit}
      leftLabel="キャンセル"
      rightLabel="OK"
      ActionButton={ActionButton}
      cancelButtonSx={{ bgcolor: "#D9D9D9" }}
      submitButtonSx={modalFooterOkButtonSx}
    />
  );

  const shouldShowSearchResults =
    hasSearchFilters && (hasExecutedSearch || isSearching);

  // 表示する一覧は「検索中は searchResults、それ以外は jobs」
  const visibleJobs = shouldShowSearchResults ? searchResults : jobs;

  // loading も検索中は isSearching を使う
  const visibleLoading = shouldShowSearchResults ? isSearching : loading;

  // pagination も検索中は検索用を使う
  const visiblePagination = shouldShowSearchResults
    ? searchPagination
    : pagination;

  useEffect(() => {
    return () => {
      jobsAbortControllerRef.current?.abort();
    };
  }, []);

  // 既読
  const handleMarkedRead = useCallback(
    (jobId) => {
      const updateJobs = (previousJobs) => updateReadFlagById(previousJobs, jobId);

      setJobs(updateJobs);
      mutateSearchResults(updateJobs);
    },
    [mutateSearchResults],
  );

  // 遷移先
  const handleClick = () => {
    navigate(`/user_job_request/edit?redirect=${window.location.pathname}`);
  };

  // ページ移動
  const goToPage = useCallback(
    (nextPage) => {
      // 検索結果表示中なら、同じ条件でそのページを再検索
      if (shouldShowSearchResults) {
        scrollToResultsTop();
        executeSearch(searchFilters, { page: nextPage, per: DEFAULT_PER_PAGE });
        return;
      }

      // 通常一覧表示中の遷移
      setPage(nextPage);
      scrollToResultsTop();
      fetchJobs(nextPage);
    },
    [
      shouldShowSearchResults,
      scrollToResultsTop,
      executeSearch,
      searchFilters,
      fetchJobs,
    ],
  );

  // データ未取得時のローディング表示
  if (isStatusLoading) {
    return (
      <Box p={4} ml={4}>
        <Typography>読み込み中...</Typography>
      </Box>
    );
  }

  return (
    <Container
      maxWidth="md"
      disableGutters
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ===== 固定エリア ===== */}
      <Box
        sx={{
          position: "sticky",
          top: isDesktop ? "63px" : "45px",
          zIndex: 20,
          backgroundColor: "#F4F7FA",
          boxShadow: 0.5,
          mx: -2,
          px: isDesktop ? 0 : 2,
          pt: isDesktop ? 1.5 : 1,
        }}
      >
        {/* --- 上段：タイトル / RequestStatus --- */}
        <Box sx={{ mb: 1 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: isDesktop ? "row" : "column",
              alignItems: isDesktop ? "center" : "stretch",
              gap: isDesktop ? 1.5 : 1,
            }}
          >
            {/* タイトル */}
            <Typography
              fontWeight="bold"
              sx={{
                fontSize: isDesktop ? "1.8rem" : "1.2rem",
                whiteSpace: "nowrap",
              }}
            >
              求人
            </Typography>

            {/* タイトル寄りの RequestStatus */}
            {jobExists && (
              <RequestStatus
                viewerType="user"
                type="job"
                isActive={isActive}
                onToggle={handleChangeStatus}
              />
            )}

            {/* NoticeBanner + ActionButton */}
            <Box
              sx={{
                ml: isDesktop ? "auto" : 0,
                display: "flex",
                flexDirection: isDesktop ? "row" : "column-reverse",
                gap: 1,
                width: isDesktop ? "auto" : "100%",
                alignItems: "center",
              }}
            >
              <NoticeBanner
                text={
                  jobExists
                    ? "希望条件に変更があれば、更新しておきましょう。"
                    : "希望条件を入力すると、詳細を全て閲覧できます。"
                }
              />

              <ActionButton
                onClick={handleClick}
                label={`希望条件を${jobExists ? "編集" : "入力"}`}
                icon={<ModeEditOutlinedIcon sx={{ color: JOB_COLOR }} />}
                showBadge={!jobExists}
                sx={{
                  bgcolor: JOB_COLOR,
                  width: isDesktop ? "auto" : "100%",
                  "&:hover": { bgcolor: JOB_COLOR_DARK },
                }}
                badgeSx={{
                  bgcolor: JOB_COLOR_LIGHT,
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* 求人一覧のみ表示 */}
        {showSearchContainer && (
          <Box sx={{ mt: 1 }}>
            <SearchContainer
              type="job"
              value={searchFilters}
              onChange={(nextFilters) => {
                const prevKeywordCount = searchFilters.keyword?.length ?? 0;
                const nextKeywordCount = nextFilters.keyword?.length ?? 0;

                if (nextKeywordCount < prevKeywordCount) {
                  scrollToResultsTop();
                }

                setSearchFilters(nextFilters);

                if (!isSearching) {
                  markSearchAsNotExecuted();
                }
              }}
              onSearch={(filters) => {
                scrollToResultsTop();
                executeSearch(filters, { page: 1, per: DEFAULT_PER_PAGE });
              }}
              onOpenFilter={handleOpen}
            />
            {isDesktop && activeFilterTags.length > 0 && (
              <Box>
                <FilterTag
                  type="job"
                  selectedKeywords={activeFilterTags}
                  onDeleteTag={(keywordIndex) => {
                    const tagToRemove = activeFilterTags[keywordIndex];
                    if (!tagToRemove) return;
                    scrollToResultsTop();
                    handleRemoveFilterTagAndSearch(tagToRemove);
                  }}
                />
                <Button
                  variant="text"
                  onClick={() => {
                    const nextFilters = {
                      ...searchFilters,
                      roleIds: [],
                      prefectureIds: [],
                      priceFilter: createInitialPriceFilter(),
                    };

                    scrollToResultsTop();
                    setSearchFilters(nextFilters);
                    executeSearch(nextFilters, {
                      page: 1,
                      per: DEFAULT_PER_PAGE,
                    });
                  }}
                  sx={{ color: GRAY_TEXT_COLOR, mt: 1 }}
                >
                  フィルターをリセット
                </Button>
              </Box>
            )}
            {isMobile ? (
              <>
                <MenuModal
                  anchorEl={menuAnchorEl}
                  open={menuOpen}
                  onClose={() => setMenuAnchorEl(null)}
                  paperSx={{ width: "100%", borderRadius: 1 }}
                >
                  <>
                    <MenuItem
                      onClick={() => {
                        openFilterModal("position"); // ModalForSp を Positionで開く
                      }}
                      sx={{ borderBottom: `1px solid ${BORDER_COLOR_LIGHT}` }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <Typography
                          sx={{
                            color:
                              searchFilters.roleIds.length > 0
                                ? TEXT_COLOR_PRIMARY
                                : GRAY_TEXT_COLOR,
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                          }}
                        >
                          {searchFilters.roleIds.length > 0
                            ? selectedRoleLabel
                            : "ポジション"}
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        openFilterModal("price"); // ModalForSp を Priceで開く
                      }}
                      sx={{ borderBottom: `1px solid ${BORDER_COLOR_LIGHT}` }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <Typography
                          sx={{
                            color:
                              selectedIncomeLabel.length > 0
                                ? TEXT_COLOR_PRIMARY
                                : GRAY_TEXT_COLOR,
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                          }}
                        >
                          {selectedIncomeLabel !== ""
                            ? selectedIncomeLabel
                            : "年収"}
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        openFilterModal("prefecture"); // ModalForSp を Prefectureで開く
                      }}
                      sx={{ borderBottom: `1px solid ${BORDER_COLOR_LIGHT}` }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <Typography
                          sx={{
                            color:
                              searchFilters.prefectureIds.length > 0
                                ? TEXT_COLOR_PRIMARY
                                : GRAY_TEXT_COLOR,
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                          }}
                        >
                          {searchFilters.prefectureIds.length > 0
                            ? selectedPrefectureLabel
                            : "勤務地"}
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        deleteFilter();
                        // NOTE: Mobile UX spec:
                        // Do NOT call setMenuAnchorEl(null) intentionally.
                        // Keep MenuModal open so users can see the currently applied conditions after closing FilterModal.
                        // Keep MenuModal open to immediately reflect the cleared conditions in this panel.
                      }}
                      sx={{ color: GRAY_TEXT_COLOR }}
                    >
                      フィルターをリセット
                    </MenuItem>
                  </>
                </MenuModal>
                <ModalSp
                  open={filterModalOpen}
                  onClose={handleClose}
                  header={({ modalTitleId }) => (
                    <FilterModalHeader
                      title={modalTitle}
                      headerSx={modalHeaderSx}
                      titleId={modalTitleId}
                    />
                  )}
                  footer={modalFooter}
                >
                  {activeFilter === "position" && (
                    <Positions
                      roles={roles}
                      selectedRoleIds={searchFilters.roleIds}
                      onChangeSelectedRoleIds={(nextRoleIds) => {
                        setSearchFilters((previousFilters) => ({
                          ...previousFilters,
                          roleIds: nextRoleIds,
                        }));
                      }}
                    />
                  )}

                  {activeFilter === "price" && (
                    <Price
                      priceFilter={searchFilters.priceFilter}
                      setPriceFilter={setPriceFilterInSearchFilters}
                      label="希望年収"
                    />
                  )}

                  {activeFilter === "prefecture" && (
                    <Prefectures
                      prefByArea={prefByArea}
                      selectedIds={searchFilters.prefectureIds}
                      setSelectedIds={(nextPrefectureIds) => {
                        setSearchFilters((previousFilters) => ({
                          ...previousFilters,
                          prefectureIds: nextPrefectureIds,
                        }));
                      }}
                      isOpen={filterModalOpen}
                    />
                  )}
                </ModalSp>
              </>
            ) : (
              <Modal
                open={filterDialogOpen}
                onClose={handleClose}
                aria-labelledby="job-filter-modal-title"
              >
                <Box sx={modalStyle}>
                  <Typography
                    id="job-filter-modal-title"
                    variant="h6"
                    sx={{
                      position: "absolute",
                      width: "1px",
                      height: "1px",
                      padding: 0,
                      margin: "-1px",
                      overflow: "hidden",
                      clip: "rect(0, 0, 0, 0)",
                      whiteSpace: "nowrap",
                      border: 0,
                    }}
                  >
                    検索条件の絞り込み
                  </Typography>
                  <SearchTabs
                    roles={roles}
                    prefByArea={prefByArea}
                    selectedRoleIds={searchFilters.roleIds}
                    setSelectedRoleIds={(nextRoleIds) => {
                      setSearchFilters((previousFilters) => ({
                        ...previousFilters,
                        roleIds: nextRoleIds,
                      }));
                    }}
                    selectedPrefectureIds={searchFilters.prefectureIds}
                    setSelectedPrefectureIds={(nextPrefectureIds) => {
                      setSearchFilters((previousFilters) => ({
                        ...previousFilters,
                        prefectureIds: nextPrefectureIds,
                      }));
                    }}
                    priceFilter={searchFilters.priceFilter}
                    setPriceFilter={setPriceFilterInSearchFilters}
                    PriceComponent={Price}
                    type="job"
                    tabLabels={{
                      roles: "ポジション",
                      price: "年収",
                      prefecture: "勤務地",
                    }}
                    onSearch={(filters) => {
                      scrollToResultsTop();
                      executeSearch(filters, {
                        page: 1,
                        per: DEFAULT_PER_PAGE,
                      });
                    }}
                    onClose={handleClose}
                  />
                </Box>
              </Modal>
            )}
          </Box>
        )}
      </Box>

      {/* ===== スクロール領域 ===== */}
      <Box
        sx={{
          flex: 1,
        }}
      >
        <Box
          id="job-search-results-top"
          tabIndex={-1}
          sx={{
            outline: "none",
            pt: isMobile ? "40px" : 0,
            mt: isMobile ? "-40px" : 0,
          }}
        >
          <Outlet
            context={{
              jobExists,
              isActive,
              jobs: visibleJobs,
              loading: visibleLoading,
              hasSearchFilters,
              hasExecutedSearch,
              isSearching,
              fetchJobs,
              setJobs,
              handleMarkedRead,
              fetchJobsBySearch: executeSearch,
              pagination: visiblePagination,
              page,
              goToPage,
            }}
          />
        </Box>
      </Box>

      {/* --- モーダル --- */}
      <ConfirmStopModal
        open={modalOpen}
        onClose={handleCancelStop}
        onConfirm={handleConfirmStop}
        title={isActive ? "スキルログ運営局に非公開" : "スキルログ運営局に公開"}
        message={
          isActive
            ? "非公開にすると運営局とのコンタクトができなくなります。"
            : "公開すると運営局からの連絡やプロジェクト紹介を受け取れます。"
        }
        confirmLabel={isActive ? "非公開にする" : "公開する"}
      />
    </Container>
  );
}
